import React, { useEffect, useRef, useState } from "react";
import PaymentOption from "./PaymentOption";
import PaymentMethod from "./PaymentMethod";
import { useNavigate } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
  PaymentMethodObjType,
  API_PAYMENT_METHODS,
  CURRENCY_TYPES,
  PAYMENT_METHODS,
  isBankWithdrawTab,
  isUpiWithdrawTab,
  isCryptoWithdrawTab,
  createNewAccount,
  deleteAccount,
  getPaymentConfig,
  getPaymentProviders,
  withdrawFunds,
  AddAccountFormType,
  AddAccountPayloadType,
  AddCryptoWalletFormType,
  AddCryptoWalletPayloadType,
  AddUpiAccountFormType,
  AddUpiAccountPayloadType,
  DeleteAccountParamsType,
  WithdrawFormType,
  cryptoCurrencyList,
  getAddAccountFromInitialState,
  getAddCryptoWalletInitialForm,
  getAddUpiAccountInitialForm,
  getInitialWithdrawForm,
  HOME_ROUTE,
  MY_TRANSACTION_ROUTE,
  showToast,
  getFieldFromToken,
  JwtToken,
  agpayUrl,
  toProviderPath,
logout,
} from "@hypexone/uicore";

import { AddAccountFormFields, WithdrawFormFields } from "./WithdrawPageForms";
import { AGPAY_API_CLIENT, SVLS_API_CLIENT } from "../../../App.utils";
import { normalizeInput } from "../Payments/commonutils";


const initialAddAccountFormState = getAddAccountFromInitialState();
const initialWithdrawForm = getInitialWithdrawForm();
const initialAddUpiAccountState = getAddUpiAccountInitialForm();
const initialAddCryptoWalletState = getAddCryptoWalletInitialForm();

interface WithdrawAmountProps {}

const WithdrawAmount: React.FC<WithdrawAmountProps> = () => {
  const { withdrawPaymentConfig } = useAppSelector((state) => state.payment);
  const { paymentMethodList } = useAppSelector((state) => state.payment);
  const { domainConfig } = useAppSelector((state) => state.common);
  const { langData } = useAppSelector((state) => state.cms);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [currentSelectedAccount, setCurrentSelectedAccount] =
    useState<PaymentMethodObjType>();

  const dispatch = useAppDispatch();

  const getPaymentMethods = (): PAYMENT_METHODS[] => {
    if (Object.keys(withdrawPaymentConfig).length !== 0) {
      return Object.keys(withdrawPaymentConfig) as PAYMENT_METHODS[];
    } else {
      return [];
    }
  };

  const [showAddAccountForm, setShowAddAccountForm] = useState(false);

  const [selectedPaymentProvider, setSelectedPaymentProvider] = useState("");
  const [receivableAmount, setReceivableAmount] = useState<number>(null);

  let [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PAYMENT_METHODS>();

  useEffect(() => {
    const paymentMethodConfig = Object.keys(withdrawPaymentConfig);
    if (paymentMethodConfig.length !== 0) {
      const provider = withdrawPaymentConfig[paymentMethodConfig[0]][0];
      // @ts-ignore
      selectedPaymentMethod = paymentMethodConfig[0];
      setSelectedPaymentMethod(selectedPaymentMethod);
      setSelectedPaymentProvider(provider);
    }
  }, [withdrawPaymentConfig, dispatch]);

  useEffect(() => {
    dispatch(getPaymentConfig());
  }, [dispatch]);

  useEffect(() => {
    if (selectedPaymentMethod) {
      dispatch(getPaymentProviders(selectedPaymentMethod));
      const provider = withdrawPaymentConfig[selectedPaymentMethod][0];
      setSelectedPaymentProvider(provider);
    }

    setCurrentSelectedAccount(null);
    resetForms();
    setShowAddAccountForm(false);
  }, [selectedPaymentMethod]);

  const [withdrawFormData, setWithdrawFormData] =
    useState<WithdrawFormType>(initialWithdrawForm);

  const [addAccountFormData, setAddAccountFormData] =
    useState<AddAccountFormType>(initialAddAccountFormState);

  const [addUpiAccountFormData, setAddUpiAccountFormData] =
    useState<AddUpiAccountFormType>(initialAddUpiAccountState);

  const [addCryptoWalletFormData, setAddCryptoWalletFormData] =
    useState<AddCryptoWalletFormType>(initialAddCryptoWalletState);

  const [otpTimer, setOtpTimer] = useState<number>();
  const [phoneNumbeErrorMsg, setPhoneNumbeErrorMsg] = useState<string>('');
  const [otpLoader, setOtpLoader] = useState<boolean>(false);
  const otpTimerIdRef = useRef(null);

  const handleOtpTimer = (time) => {
    if (otpTimerIdRef.current) {
      clearTimeout(otpTimerIdRef.current);
    }

    if (time === 0) {
      setOtpTimer(undefined);
      return;
    }

    if (time > 0) {
      setOtpTimer(time);
      otpTimerIdRef.current = setTimeout(() => {
        handleOtpTimer(time - 1);
      }, 1000);
    }
  };

  const stopTimer = () => {
    if (otpTimerIdRef.current) {
      clearTimeout(otpTimerIdRef.current);
      otpTimerIdRef.current = null;
    }
    setOtpTimer(0);
  };

  useEffect(() => {
    let mode = getFieldFromToken(JwtToken.MODE);
    if(!mode) {
      dispatch(logout())
    }
  },[])

  const isOnlineUser = getFieldFromToken(JwtToken.MODE)?.toLowerCase() === 'online';

  const sendOtp = async () => {
    setPhoneNumbeErrorMsg('');
    setOtpLoader(true);
    try {
      const response = await SVLS_API_CLIENT.post(
        `/account/v2/users/otp`,
        {},
        {
          headers: {
            Authorization: sessionStorage.getItem('jwt_token'),
          },
        }
      );
      handleOtpTimer(60);
    } catch (err) {
      setPhoneNumbeErrorMsg(err?.response?.data?.message);
    }
    setOtpLoader(false);
  };

  const handleWithdrawFormChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    withdrawFormData[event.target.id] = event.target.value;
    setWithdrawFormData({ ...withdrawFormData });
  };

  const handleAddAccountFormChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    addAccountFormData[event.target.id] = event.target.value;
    setAddAccountFormData({ ...addAccountFormData });
  };

  const handleAddUpiAccountFormChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    addUpiAccountFormData[event.target.id] = event.target.value;
    setAddUpiAccountFormData({ ...addUpiAccountFormData });
  };

  const resetForms = () => {
    // TODO: Make this work with initialFormData from utils
    stopTimer();
    setAddAccountFormData({
      accountHolderName: "",
      accountNumber: "",
      bankName: "",
      branchName: "",
      ifscCode: "",
      displayName: "",
      otp: "",
    });
    setWithdrawFormData({ amount: "", notes: "" });
    setAddCryptoWalletFormData({
      selectedCryptoCurrency: null,
      walletAddress: "",
      otp: "",
    });
  };

  const isWithdrawSaveBtnDisabled = !(
    withdrawFormData.amount && withdrawFormData.notes
  );

  const onPaymentMethodClick = (paymentMethod: PAYMENT_METHODS) => {
    setSelectedPaymentMethod(paymentMethod);
    setReceivableAmount(null);
  };

  const checkAddAccountSaveBtnEnabledStatus = () => {
    if (isBankWithdrawTab(selectedPaymentMethod)) {
      return !(
        addAccountFormData.accountNumber &&
        addAccountFormData.accountHolderName &&
        addAccountFormData.displayName &&
        addAccountFormData.bankName &&
        addAccountFormData.branchName &&
        addAccountFormData.ifscCode
      );
      // @ts-ignore
    } else if (isUpiWithdrawTab(selectedPaymentMethod)) {
      return !(
        addUpiAccountFormData.accountHolderName &&
        addUpiAccountFormData.displayName &&
        addUpiAccountFormData.upiId
      );
    } else {
      return !(
        addCryptoWalletFormData.selectedCryptoCurrency &&
        addCryptoWalletFormData.walletAddress
      );
    }
  };

  const isAddAccountSaveBtnDisabled = checkAddAccountSaveBtnEnabledStatus();

  const onSuccessCallback = () => {
    resetForms();
    navigate(MY_TRANSACTION_ROUTE);
  };

  const getReceivableAmount = async () => {
    if (!withdrawFormData.amount) {
      dispatch(
        showToast({
          type: "error",
          message: langData?.["enter_amount"],
        })
      );
      return false;
    }
    try {
      const payload = {
        amount: +withdrawFormData.amount,
      };
      setLoading(true);
      let paymentProvider = selectedPaymentProvider?.toLowerCase() === "zenpaycrypto"
        ? "jpc"
        : selectedPaymentProvider?.toLowerCase() === "zenpaycryptoseamless" ? "jpcseamless" : selectedPaymentProvider;
      const response = await AGPAY_API_CLIENT.post(
        agpayUrl(toProviderPath(paymentProvider), 'exchange-rates'),
        payload,
        {
          headers: {
            Authorization: sessionStorage.getItem("jwt_token"),
          },
        }
      );
      if (response.status === 200) {
        response.data?.data.map((currency) => {
          if (
            currency?.network_id === currentSelectedAccount.queueMethodDetails.networkId &&
            currency?.crypto_currency === currentSelectedAccount.queueMethodDetails.cryptoCurrency
          ) {
            setReceivableAmount(currency?.receivable_amount);
          }
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawFormSubmission = (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    // Use this handler to implement exch rates call for crypto
    handleWithdrawFunds(e);
  };

  const handleWithdrawFunds = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLoading(true);
      let payload;
      if (isBankWithdrawTab(selectedPaymentMethod)) {
        payload = {
          selectedPaymentProvider: selectedPaymentProvider,
          amount: withdrawFormData.amount,
          notes: withdrawFormData.notes,
          queue_source_id: currentSelectedAccount.id.toString(),
          queue_source: PAYMENT_METHODS.BANK_TRANSFER,
          cr_type: CURRENCY_TYPES.INR,
          mobile_number: "9876543211",
          onSuccessCallback: onSuccessCallback,
        };
      } else if (isUpiWithdrawTab(selectedPaymentMethod)) {
        payload = {
          selectedPaymentProvider: selectedPaymentProvider,
          amount: withdrawFormData.amount,
          notes: withdrawFormData.notes,
          queue_source_id: currentSelectedAccount.id.toString(),
          cr_type: CURRENCY_TYPES.INR,
          onSuccessCallback: onSuccessCallback,
        };
      } else {
        payload = {
          selectedPaymentProvider:
            selectedPaymentProvider?.toLowerCase() === "zenpaycrypto"
              ? "jpc"
              : selectedPaymentProvider?.toLowerCase() === "zenpaycryptoseamless" ? "jpcseamless" : selectedPaymentProvider,
          amount: withdrawFormData.amount,
          notes: withdrawFormData.notes,
          queue_source_id: currentSelectedAccount.id.toString(),
          onSuccessCallback: onSuccessCallback,
          cr_type: CURRENCY_TYPES.INR,
        };
      }

      await dispatch(withdrawFunds(payload)).unwrap();
    } catch (err) {
      console.log("Error: ", err);
    } finally {
      setLoading(false);
    }
  };

  const addAccountSuccess = () => {
    dispatch(getPaymentProviders(selectedPaymentMethod));
    resetForms();
  };

  const onAddAccountSaveBtnClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLoading(true);
      let payload:
        | AddUpiAccountPayloadType
        | AddAccountPayloadType
        | AddCryptoWalletPayloadType = null;
      if (isBankWithdrawTab(selectedPaymentMethod)) {
        if (isOnlineUser && !addAccountFormData.otp || addAccountFormData.otp.length > 6) {
          dispatch(
            showToast({
              message: "Please enter valid OTP",
              type: "error",
            }))
          return;
        }
        payload = {
          accountHolderName: normalizeInput(addAccountFormData.accountHolderName),
          accountNumber: normalizeInput(addAccountFormData.accountNumber),
          bankName: normalizeInput(addAccountFormData.bankName),
          branchName: normalizeInput(addAccountFormData.branchName),
          displayName: normalizeInput(addAccountFormData.displayName),
          ifscCode: normalizeInput(addAccountFormData.ifscCode),
          queueMethod: PAYMENT_METHODS.BANK_TRANSFER,
          otp: addAccountFormData.otp,
          onSuccessCallback: addAccountSuccess,
        };
      }
      //@ts-ignore
      else if (isUpiWithdrawTab(selectedPaymentMethod)) {
        if (isOnlineUser && !addUpiAccountFormData.otp || addUpiAccountFormData.otp.length > 6) {
          dispatch(
            showToast({
              message: "Please enter valid OTP",
              type: "error",
            }))
          return;
        }
        payload = {
          accountHolderName: normalizeInput(addUpiAccountFormData.accountHolderName),
          accountNumber: normalizeInput(addUpiAccountFormData.upiId),
          displayName: normalizeInput(addUpiAccountFormData.displayName),
          ifscCode: "",
          queueMethod: PAYMENT_METHODS.UPI,
          otp: addUpiAccountFormData.otp,
          onSuccessCallback: addAccountSuccess,
        };
      } else {
        if (isOnlineUser && !addCryptoWalletFormData.otp || addCryptoWalletFormData.otp.length > 6) {
          dispatch(
            showToast({
              message: "Please enter valid OTP",
              type: "error",
            }))
          return;
        }
        const {
          selectedCryptoCurrency: { blockchain, crypto_currency, network_id },
        } = addCryptoWalletFormData;
        payload = {
          queueMethod: PAYMENT_METHODS.CRYPTO_WALLET_TRANSFER,
          accountNumber: normalizeInput(addCryptoWalletFormData.walletAddress),
          cryptoCurrencyType: crypto_currency,
          blockchain: blockchain,
          networkId: network_id,
          otp: addCryptoWalletFormData.otp,
          onSuccessCallback: addAccountSuccess,
        };
      }
      dispatch(createNewAccount(payload));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const onSavedAccountCardClick = (account: PaymentMethodObjType) => {
    setShowAddAccountForm(false);
    setCurrentSelectedAccount(account);
    setReceivableAmount(null);
    setWithdrawFormData({
      amount: "",
      notes: ""
    });
  };

  const onAddNewAccountPress = () => {
    setCurrentSelectedAccount(null);
    setShowAddAccountForm(true);
  };

  const onAccountDeleteSuccess = () => {
    dispatch(getPaymentProviders(selectedPaymentMethod));
    setCurrentSelectedAccount(null);
  };

  const onAccountDeleteClick = (account: PaymentMethodObjType) => {
    const deleteAccountParams: DeleteAccountParamsType = {
      paymentMethodId: account.id.toString(),
      onSuccessCallback: onAccountDeleteSuccess,
    };
    dispatch(deleteAccount(deleteAccountParams));
  };

  const handlePaymentProviderSelect = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedPaymentProvider(e.target.value);
  };

  useEffect(() => {
    if (!domainConfig.payments) {
      navigate(HOME_ROUTE, { replace: true });
    }
  }, []);

  //TODO: Create a custom hook for all above functionalities and separate the logic and UI

  return (
    <section className="flex flex-col items-start pb-44 bg-white rounded border border-solid border-black border-opacity-10 max-md:pb-24 md:p-0">
      <header className="self-stretch px-4 py-1.5 text-lg max-md:text-[18px] text-white rounded bg-section-gradient max-md:pr-5 max-md:max-w-full">
        {langData?.["withdraw_amount"]}
      </header>
      {withdrawPaymentConfig &&
        withdrawPaymentConfig[selectedPaymentMethod] &&
        withdrawPaymentConfig[selectedPaymentMethod].length > 0 && (
          <>
            <div className="flex flex-col mt-4 md:ml-4 max-w-full w-full">
              <PaymentMethod
                methods={getPaymentMethods()}
                onPaymentMethodClick={onPaymentMethodClick}
                selectedPaymentMethod={selectedPaymentMethod}
              />

              <label htmlFor={"provider-select"} className="self-start mt-4">
                {langData?.["select_provider"]}
              </label>

              <select
                id="provider-select"
                className="block w-full md:w-[40%] p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                onChange={handlePaymentProviderSelect}
                defaultValue={withdrawPaymentConfig[selectedPaymentMethod][0]}
              >
                {withdrawPaymentConfig &&
                  withdrawPaymentConfig[selectedPaymentMethod] &&
                  withdrawPaymentConfig[selectedPaymentMethod].length > 0 &&
                  withdrawPaymentConfig[selectedPaymentMethod].map(
                    (option, index) => {
                      return (
                        <option key={index} value={option}>
                          {`${langData?.["option"]} ${index + 1}`}
                        </option>
                      );
                    }
                  )}
              </select>

              <p className="text-base text-black max-md:max-w-full mt-2">
                {langData?.["select_payment_option"]}:
              </p>
              <div className="flex items-center">
                {paymentMethodList && paymentMethodList.length > 0 ? (
                  <div className="flex gap-3 items-center mt-3 max-w-[75%] overflow-scroll p-2">
                    {paymentMethodList.map((method, index) => (
                      <PaymentOption
                        key={index}
                        paymentMethod={method}
                        onAccountDeleteClick={onAccountDeleteClick}
                        onSavedAccountCardClick={onSavedAccountCardClick}
                        currentSelectedAccount={currentSelectedAccount}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex gap-3 items-center mt-3 max-w-[75%] justify-center">
                    {langData?.["no_accounts_found"]}
                  </div>
                )}

                <div
                  onClick={onAddNewAccountPress}
                  className="flex flex-col rounded-xl border border-dashed border-stone-300 justify-center items-center w-32 h-40 cursor-pointer"
                >
                  <div className="bg-zinc-100 w-8 h-8 flex items-center justify-center rounded-full text-lg">
                    +
                  </div>
                  <div className="mt-2.5 text-xs md:text-sm text-black">
                    {langData?.["add_new_account"]}
                  </div>
                </div>
              </div>
            </div>

            {/* Forms start here */}
            {showAddAccountForm ? (
              <AddAccountFormFields
                addAccountFormData={addAccountFormData}
                addUpiAccountFormData={addUpiAccountFormData}
                handleAddAccountFormChange={handleAddAccountFormChange}
                handleAddUpiAccountFormChange={handleAddUpiAccountFormChange}
                isSaveBtnDisabled={isAddAccountSaveBtnDisabled}
                onSave={onAddAccountSaveBtnClick}
                resetForms={resetForms}
                selectedPaymentMethod={selectedPaymentMethod}
                addCryptoWalletFormData={addCryptoWalletFormData}
                setAddCryptoWalletFormData={setAddCryptoWalletFormData}
                loading={loading}
                otpLoader={otpLoader}
                sendOtp={sendOtp}
                otpTimer={otpTimer}
                phoneNumbeErrorMsg={phoneNumbeErrorMsg}
                selectedPaymentProvider={selectedPaymentProvider}
              />
            ) : (
              <>
                {currentSelectedAccount && (
                  <WithdrawFormFields
                    handleWithdrawFormChange={handleWithdrawFormChange}
                    withdrawFormData={withdrawFormData}
                    isWithdrawSaveBtnDisabled={isWithdrawSaveBtnDisabled}
                    loading={loading}
                    onSave={handleWithdrawFormSubmission}
                    getReceivableAmount={getReceivableAmount}
                    receivableAmount={receivableAmount}
                    currentSelectedAccount={currentSelectedAccount}
                    paymentProvider={selectedPaymentProvider}
                  />
                )}
              </>
            )}
          </>
        )}
    </section>
  );
};

export default WithdrawAmount;
